// frontend/src/components/ProjectDetail/index.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  BookOpen,
  Briefcase,
  Compass,
  CreditCard,
  Flag,
  FolderOpen,
  Map,
  Mic,
  Sparkles,
  Package,
  Plus,
  Target,
  Wrench,
} from "lucide-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../Auth";
import ProjectHeader from "./ProjectHeader";
import MobileRoadmap from "./MobileRoadmap";
import ProjectTimeline from "./ProjectTimeline";
import MilestoneObjectiveModal from "./MilestoneObjectiveModal";
import AttributesPanel from "./panels/AttributesPanel";
import AffairesPanel from "./panels/AffairesPanel";
import ExpensesPanel from "./panels/ExpensesPanel";
import { ProjectPurchasesPanel } from "../Expenses/ProjectPurchases";
import JournalPanel from "./panels/JournalPanel";
import DriveBrowserPanel from "./panels/DriveBrowserPanel";
import ProjectInvoicesPanel from "./panels/ProjectInvoicesPanel";
import GuttenbergProjectPanel from "../Guttenberg/GuttenbergProjectPanel";
import { ProjectMeetings } from "../Meetings";
import TalkingPointsPanel from "../Home/TalkingPointsPanel";
import MuseCarnet from "./MuseCarnet";
import ProjectLexicon from "./ProjectLexicon";
import { apiService } from "../../services/api";
import { useNexusSync } from "../../context/NexusSyncContext";
import { parseProjectJournalLogMessage } from "../../utils/stockLogLinks";

const SIDE_NAV_ITEMS = [
  { key: "overview", label: "Vue globale", icon: Compass },
  { key: "roadmap", label: "Roadmap", icon: Map },
  { key: "attributes", label: "Attributs", icon: Target },
  { key: "journal", label: "Journal", icon: BookOpen },
  { key: "meetings", label: "Réunions", icon: Mic },
  { key: "carnet", label: "Carnet de Nexus", icon: Sparkles },
  { key: "drive", label: "Drive", icon: FolderOpen },
  { key: "tools", label: "Outils", icon: Wrench },
  { key: "expenses", label: "Dépenses & achats", icon: CreditCard },
  { key: "project-items", label: "Items du projet", icon: Package },
  { key: "affaires", label: "Affaires & offres", icon: Briefcase },
];

const apiBaseUrl =
  process.env.NODE_ENV === "development"
    ? `${process.env.REACT_APP_API_URL || "http://localhost:8000"}`
    : `${process.env.REACT_APP_API_URL}/api` || "http://localhost:8000";

const DRIVE_KNOWLEDGE_SYNC_TTL_MS = 15 * 60 * 1000;

const formatDateInput = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ProjectDetail = () => {
  const { projectId } = useParams();

  useEffect(() => {
    if (projectId) apiService.post(`/home/visits/${projectId}`, {}).catch(() => {});
  }, [projectId]);
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeaders, user } = useAuth();

  // --- State Management ---
  const [project, setProject] = useState(null);
  const [iterations, setIterations] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [circles, setCircles] = useState([]);
  const [projectRoleFocus, setProjectRoleFocus] = useState([]);
  const [slots, setSlots] = useState([]);
  const [affaires, setAffaires] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [milestoneEditor, setMilestoneEditor] = useState(null);
  const [milestoneSaving, setMilestoneSaving] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [projectStockItems, setProjectStockItems] = useState([]);
  const [projectStockSearch, setProjectStockSearch] = useState("");
  const [projectStockLoading, setProjectStockLoading] = useState(false);
  const [projectStockSelectedId, setProjectStockSelectedId] = useState(null);
  const [projectStockSavingId, setProjectStockSavingId] = useState(null);
  const [projectStockChildren, setProjectStockChildren] = useState({});
  const [projectStockLoadingChildren, setProjectStockLoadingChildren] = useState(
    {},
  );
  const [projectStockExpandedNodes, setProjectStockExpandedNodes] = useState({});
  const [projectStockLogs, setProjectStockLogs] = useState({});
  const [projectStockLogMessage, setProjectStockLogMessage] = useState({});
  const [projectStockInlineEdits, setProjectStockInlineEdits] = useState({});
  const [attributes, setAttributes] = useState([]);
  const [attributeHistory, setAttributeHistory] = useState([]);
  const [attributeTargets, setAttributeTargets] = useState([]);
  const [attributeError, setAttributeError] = useState(null);
  const [attributeLoading, setAttributeLoading] = useState(false);
  const [attributeHistoryLoading, setAttributeHistoryLoading] = useState(false);
  const [sprintHours, setSprintHours] = useState({});
  const [error, setError] = useState(null);

  const projectStockStatusOptions = [
    { value: "ordered", label: "Commande" },
    { value: "in_stock", label: "En stock" },
    { value: "at_client", label: "Chez le proprietaire" },
    { value: "removed", label: "Supprime" },
  ];

  const projectStockStatusClassMap = {
    ordered: "bg-amber-100 text-amber-700",
    in_stock: "bg-emerald-100 text-emerald-700",
    at_client: "bg-sky-100 text-sky-700",
    removed: "bg-rose-100 text-rose-700",
  };

  const getProjectStockStatusLabel = (status) => {
    const match = projectStockStatusOptions.find((option) => option.value === status);
    return match ? match.label : status;
  };

  const getProjectStockLabel = (item) => {
    if (!item) return "";
    const base = item.item_name || "";
    return item.occurrence_name ? `${base} - ${item.occurrence_name}` : base;
  };

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window === "undefined") return 780;
    const maxAllowedWidth = Math.min(window.innerWidth * 0.45, 980);
    return Math.max(520, Math.floor(maxAllowedWidth));
  });

  const [selectedItemId, setSelectedItemId] = useState({
    type: null,
    id: null,
  });
  const [timelineViewLevel, setTimelineViewLevel] = useState("project");
  const sideNavItems = SIDE_NAV_ITEMS;
  const quickNavItems = sideNavItems.map(({ key, label }) => ({ key, label }));
  const [activeSection, setActiveSection] = useState("overview");
  const pendingSelectionRef = useRef(null);
  const { lastMutation } = useNexusSync();
  const affairesPanelRef = useRef(null);
  const expensesPanelRef = useRef(null);
  const journalPanelRef = useRef(null);
  const drivePanelRef = useRef(null);
  const toolsPanelRef = useRef(null);
  const projectItemsPanelRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === "undefined") return;
      const maxAllowedWidth = Math.min(window.innerWidth * 0.45, 980);
      setSidebarWidth(Math.max(520, Math.floor(maxAllowedWidth)));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const focusPanelSection = useCallback(
    (panelKey) => {
      if (!panelKey) return;
      const refMap = {
        affaires: affairesPanelRef,
        expenses: expensesPanelRef,
        journal: journalPanelRef,
        drive: drivePanelRef,
        tools: toolsPanelRef,
        "project-items": projectItemsPanelRef,
      };
      const target = refMap[panelKey]?.current || document.getElementById(panelKey);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [affairesPanelRef, expensesPanelRef, journalPanelRef, drivePanelRef, toolsPanelRef, projectItemsPanelRef],
  );

  // Parse deep link query params like ?panel=iterations&iterationId=123
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const panel = params.get("panel");
    const iterationId = params.get("iterationId");
    const allowedPanels = new Set([
      "affaires",
      "expenses",
      "journal",
      "meetings",
      "carnet",
      "drive",
      "tools",
      "project-items",
    ]);
    if (panel && allowedPanels.has(panel)) focusPanelSection(panel);
    if (iterationId) {
      const idNum = Number(iterationId);
      pendingSelectionRef.current = { type: "iteration", id: idNum };
      setSelectedItemId({ type: "iteration", id: idNum });
    }
  }, [location.search, focusPanelSection]);

  useEffect(() => {
    const sections = sideNavItems
      .map((item) => document.getElementById(item.key))
      .filter(Boolean);
    if (!sections.length) return undefined;
    let ticking = false;
    const updateActiveSection = () => {
      const viewportCenter = window.scrollY + window.innerHeight / 2;
      let closestId = sections[0].id;
      let minDistance = Infinity;
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const sectionCenter = rect.top + window.scrollY + rect.height / 2;
        const distance = Math.abs(sectionCenter - viewportCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestId = section.id;
        }
      });
      setActiveSection(closestId);
      ticking = false;
    };
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateActiveSection);
        ticking = true;
      }
    };
    updateActiveSection();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sideNavItems, project, iterations, attributes, journalEntries, expenses]);

  // --- Utility Functions ---
  const displayMessage = useCallback((setter, message, duration = 3000) => {
    setter(message);
    setTimeout(() => setter(null), duration);
  }, []);

  // --- Data Fetching ---
  const fetchData = useCallback(
    async (url, setter, errorMsg, processor = (data) => data) => {
      try {
        const data = await apiService.get(
          url.startsWith("/") ? url.substring(1) : url,
        );
        setter(processor(data));
      } catch (err) {
        console.error(`Error fetching ${url}:`, err);
        displayMessage(setError, `${errorMsg}: ${err.message}`);
        if (typeof processor() === "object" && !Array.isArray(processor())) {
          setter(processor({}) || null);
        } else {
          setter(processor([]) || []);
        }
      }
    },
    [apiService, displayMessage],
  );

  const fetchProjectDetails = useCallback(async () => {
    try {
      const data = await apiService.get(`/projects/${projectId}`);
      setProject(data || null);
    } catch (err) {
      const message = err?.message || "Erreur chargement projet";
      console.error("Erreur chargement projet:", err);
      if (/acc[eè]s interdit/i.test(message)) {
        navigate("/projects", {
          replace: true,
          state: { accessDeniedMessage: "Vous n'avez pas accès à ce projet." },
        });
        return;
      }
      displayMessage(setError, `Erreur chargement projet: ${message}`);
      setProject(null);
    }
  }, [projectId, apiService, displayMessage, navigate]);
  const fetchClients = useCallback(
    () =>
      fetchData(
        `/clients`,
        setClients,
        "Erreur chargement clients",
        (data) => data || [],
      ),
    [fetchData],
  );
  const fetchEmployees = useCallback(
    () =>
      fetchData(
        `/employees`,
        setEmployees,
        "Erreur chargement employés",
        (data) => (Array.isArray(data) ? data : []),
      ),
    [fetchData],
  );
  const fetchCircles = useCallback(
    () =>
      fetchData(
        `/circles`,
        setCircles,
        "Erreur chargement cercles",
        (data) => (Array.isArray(data) ? data : []),
      ),
    [fetchData],
  );
  const fetchSlots = useCallback(
    () =>
      fetchData(`/slots`, setSlots, "Erreur chargement slots", (data) =>
        Array.isArray(data)
          ? [...data].sort(
            (a, b) => new Date(a.start_date) - new Date(b.start_date),
          )
          : [],
      ),
    [fetchData],
  );
  const fetchAffaires = useCallback(
    () =>
      fetchData(
        `/affaires?project_id=${projectId}`,
        setAffaires,
        "Erreur chargement offres",
        (data) => data?.items || [],
      ),
    [projectId, fetchData],
  );
  const fetchObjectives = useCallback(
    () =>
      fetchData(
        `/projects/${projectId}/objectives`,
        setObjectives,
        "Erreur chargement objectifs",
        (data) => data?.items || data || [],
      ),
    [projectId, fetchData],
  );
  const fetchExpenses = useCallback(
    () =>
      fetchData(
        `/expenses?project_id=${projectId}`,
        setExpenses,
        "Erreur chargement dépenses",
        (data) => data?.items || [],
      ),
    [projectId, fetchData],
  );
  const fetchJournalEntries = useCallback(
    () =>
      fetchData(
        `/projects/${projectId}/journal`,
        setJournalEntries,
        "Erreur chargement journal",
        (data) => data || [],
      ),
    [projectId, fetchData],
  );
  const fetchProjectStock = useCallback(async () => {
    if (!projectId) return;
    setProjectStockLoading(true);
    try {
      const data = await apiService.get(`/inventory/stock?project_id=${projectId}`);
      setProjectStockItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setProjectStockItems([]);
      displayMessage(
        setError,
        `Erreur chargement items projet: ${err.message}`,
      );
    } finally {
      setProjectStockLoading(false);
    }
  }, [projectId, apiService, displayMessage]);

  const filteredProjectStockItems = useMemo(() => {
    const needle = projectStockSearch.trim().toLowerCase();
    if (!needle) return projectStockItems;
    return projectStockItems.filter((item) => {
      const haystack = [
        item.serial_number,
        item.item_name,
        item.occurrence_name,
        item.location,
        item.notes,
        item.tracking_number,
        item.client_name,
        item.project_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [projectStockItems, projectStockSearch]);

  const selectedProjectStockItem = useMemo(
    () =>
      filteredProjectStockItems.find(
        (item) => item.id === projectStockSelectedId,
      ) || null,
    [filteredProjectStockItems, projectStockSelectedId],
  );

  const updateProjectStockInlineEdit = (stockId, key, value) => {
    setProjectStockInlineEdits((prev) => ({
      ...prev,
      [stockId]: {
        ...prev[stockId],
        [key]: value,
      },
    }));
  };

  const getProjectStockInlineValue = (item, key) => {
    if (!item) return "";
    return projectStockInlineEdits[item.id]?.[key] ?? item[key] ?? "";
  };

  const applyProjectStockUpdate = (stockId, fields) => {
    setProjectStockItems((prev) =>
      prev.map((item) => (item.id === stockId ? { ...item, ...fields } : item)),
    );
    setProjectStockChildren((prev) => {
      let hasChanges = false;
      const next = {};
      Object.entries(prev).forEach(([parentId, children]) => {
        let updated = children;
        if (Array.isArray(children)) {
          let childUpdated = false;
          updated = children.map((child) => {
            if (child.id !== stockId) return child;
            childUpdated = true;
            return { ...child, ...fields };
          });
          if (childUpdated) {
            hasChanges = true;
          }
        }
        next[parentId] = updated;
      });
      return hasChanges ? next : prev;
    });
  };

  const persistProjectStockInline = async (item) => {
    if (!item) return;
    const edits = projectStockInlineEdits[item.id];
    if (!edits) return;
    const nextOccurrenceName = getProjectStockInlineValue(item, "occurrence_name");
    const nextStatus = getProjectStockInlineValue(item, "status");
    const nextLocation = getProjectStockInlineValue(item, "location");
    const nextNotes = getProjectStockInlineValue(item, "notes");
    const hasChanges =
      (nextOccurrenceName || "") !== (item.occurrence_name || "") ||
      nextStatus !== item.status ||
      (nextLocation || "") !== (item.location || "") ||
      (nextNotes || "") !== (item.notes || "");
    if (!hasChanges) {
      setProjectStockInlineEdits((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      return;
    }
    await handleProjectStockInlineUpdate(item, {
      occurrence_name: nextOccurrenceName || null,
      status: nextStatus,
      location: nextLocation || null,
      notes: nextNotes || null,
    });
  };

  const renderProjectStockInlineFields = (item) => (
    <div className="mt-2 grid grid-cols-1 gap-1 md:grid-cols-4 text-xs">
      <label className="space-y-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Nom</span>
        <input
          type="text"
          value={getProjectStockInlineValue(item, "occurrence_name")}
          onChange={(event) =>
            updateProjectStockInlineEdit(item.id, "occurrence_name", event.target.value)
          }
          onBlur={() => persistProjectStockInline(item)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
          placeholder="Optionnel"
        />
      </label>
      <label className="space-y-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Statut</span>
        <select
          value={getProjectStockInlineValue(item, "status")}
          onChange={(event) =>
            updateProjectStockInlineEdit(item.id, "status", event.target.value)
          }
          onBlur={() => persistProjectStockInline(item)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
        >
          {projectStockStatusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <label className="space-y-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">
          Emplacement
        </span>
        <input
          type="text"
          value={getProjectStockInlineValue(item, "location")}
          onChange={(event) =>
            updateProjectStockInlineEdit(item.id, "location", event.target.value)
          }
          onBlur={() => persistProjectStockInline(item)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
        />
      </label>
      <label className="space-y-1 md:col-span-4">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Notes</span>
        <textarea
          rows={1}
          value={getProjectStockInlineValue(item, "notes")}
          onChange={(event) =>
            updateProjectStockInlineEdit(item.id, "notes", event.target.value)
          }
          onBlur={() => persistProjectStockInline(item)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
        />
      </label>
      {projectStockSavingId === item.id && (
        <div className="text-[11px] text-slate-400 md:col-span-3">
          Sauvegarde...
        </div>
      )}
    </div>
  );

  const renderProjectStockLogMessage = (message) => {
    const { text, link } = parseProjectJournalLogMessage(message || "");
    if (!link) {
      return <div className="text-slate-700">{text}</div>;
    }
    const href = `/project/${link.projectId}?panel=journal&entryId=${link.entryId}`;
    return (
      <a
        href={href}
        className="text-slate-700 underline decoration-dotted hover:text-blue-600"
      >
        {text || "Voir le journal"}
      </a>
    );
  };

  const renderProjectStockNode = (item, depth = 0) => {
    if (!item) return null;
    const children = projectStockChildren[item.id] || [];
    const isExpanded = projectStockExpandedNodes[item.id] || depth === 0;
    const logs = projectStockLogs[item.id] || [];
    return (
      <div
        className={`rounded-2xl border border-slate-200/80 bg-white p-3 ${
          depth > 0 ? "mt-3" : ""
        }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              if (item.child_count) {
                toggleProjectStockNode(item.id);
                fetchProjectStockLogs(item.id);
              }
            }}
            className="text-left"
          >
            <div className="text-sm font-semibold text-slate-900">
              {item.serial_number || `#${item.id}`} - {getProjectStockLabel(item)}
            </div>
            <div className="text-[11px] text-slate-500">
              {item.location ? `Emplacement: ${item.location}` : ""}
            </div>
          </button>
          <div className="flex items-center gap-2 text-xs">
            <span
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                projectStockStatusClassMap[item.status] ||
                "bg-gray-100 text-gray-600"
              }`}
            >
              {getProjectStockStatusLabel(item.status)}
            </span>
            {item.child_count ? (
              <button
                type="button"
                onClick={() => {
                  toggleProjectStockNode(item.id);
                  fetchProjectStockLogs(item.id);
                }}
                className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
              >
                {isExpanded ? "Masquer" : `Sous-items (${item.child_count})`}
              </button>
            ) : null}
          </div>
        </div>
        <div className="mt-2 grid gap-2 md:grid-cols-[1fr_1fr]">
          <div>
            {renderProjectStockInlineFields(item)}
            {isExpanded && (
              <div className="mt-2 space-y-2 border-l border-slate-200/70 pl-3">
                {projectStockLoadingChildren[item.id] && (
                  <div className="text-[11px] text-slate-400">Chargement...</div>
                )}
                {!projectStockLoadingChildren[item.id] &&
                  children.length === 0 && (
                    <div className="text-[11px] text-slate-400">
                      Aucun sous-item.
                    </div>
                  )}
                {children.map((child) => renderProjectStockNode(child, depth + 1))}
              </div>
            )}
          </div>
          <div className="rounded-xl border border-slate-200/70 bg-slate-50/70 p-3 text-[11px] text-slate-500">
            <div className="flex items-center justify-between">
              <span className="uppercase tracking-wide text-[10px] text-slate-400">
                Journal
              </span>
            </div>
            <div className="mt-2 max-h-48 space-y-1 overflow-auto">
              {logs.map((log) => (
                <div key={log.id} className="rounded border bg-white px-2 py-1">
                  {renderProjectStockLogMessage(log.message)}
                  <div className="text-[10px] text-slate-400">
                    {log.created_by || "-"} -{" "}
                    {new Date(log.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
              {logs.length === 0 && (
                <div className="text-slate-400">Aucune note</div>
              )}
            </div>
            <div className="mt-2 flex gap-1">
              <input
                type="text"
                placeholder="Ajouter une note"
                value={projectStockLogMessage[item.id] || ""}
                onChange={(event) =>
                  setProjectStockLogMessage((prev) => ({
                    ...prev,
                    [item.id]: event.target.value,
                  }))
                }
                className="flex-1 rounded border border-slate-200 px-2 py-1 text-[11px]"
              />
              <button
                type="button"
                onClick={() => handleProjectStockAddLog(item.id)}
                className="rounded bg-slate-900 px-2 py-1 text-[10px] text-white"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const sortAttributes = useCallback((items = []) => {
    return [...items].sort((a, b) => {
      const orderA = typeof a.sort_order === "number" ? a.sort_order : 0;
      const orderB = typeof b.sort_order === "number" ? b.sort_order : 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.id || 0) - (b.id || 0);
    });
  }, []);

  const fetchAttributes = useCallback(async () => {
    if (!projectId) return;
    setAttributeLoading(true);
    try {
      const data = await apiService.getProjectAttributes(projectId);
      setAttributes(sortAttributes(Array.isArray(data) ? data : []));
      setAttributeError(null);
    } catch (err) {
      console.error("Erreur chargement attributs projet:", err);
      setAttributes([]);
      setAttributeError(
        err.message || "Erreur lors du chargement des attributs.",
      );
      displayMessage(
        setError,
        err.message || "Erreur lors du chargement des attributs.",
      );
    } finally {
      setAttributeLoading(false);
    }
  }, [projectId, apiService, sortAttributes, displayMessage]);

  const fetchAttributeHistory = useCallback(async () => {
    if (!projectId) return;
    setAttributeHistoryLoading(true);
    try {
      const data = await apiService.getProjectAttributeHistory(projectId, 200);
      setAttributeHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur chargement timeline attributs:", err);
      setAttributeHistory([]);
    } finally {
      setAttributeHistoryLoading(false);
    }
  }, [projectId, apiService]);

  const fetchAttributeTargets = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await apiService.getProjectAttributeTargets(projectId);
      setAttributeTargets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur chargement cibles attributs:", err);
      setAttributeTargets([]);
    }
  }, [projectId, apiService]);

  const fetchIterationsAndSprints = useCallback(async () => {
    try {
      const iterationsData = await apiService.get(
        `projects/${projectId}/iterations`,
      );
      const processedIterations = (
        Array.isArray(iterationsData) ? iterationsData : []
      ).map((iteration, iterIndex) => {
        const sortedSprints = (
          Array.isArray(iteration.sprints) ? iteration.sprints : []
        )
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
          .map((sprint, sprintIndex) => ({
            ...sprint,
            name: sprint.description || `Sprint ${sprintIndex + 1}`,
          }));
        return {
          ...iteration,
          name: iteration.name || `Itération ${iterIndex + 1}`,
          sprints: sortedSprints,
        };
      });
      setIterations(processedIterations);
      // Apply pending selection if any after iterations are loaded
      if (
        pendingSelectionRef.current &&
        pendingSelectionRef.current.type === "iteration"
      ) {
        setSelectedItemId({ ...pendingSelectionRef.current });
      }
    } catch (err) {
      console.error("Error fetching iterations/sprints:", err);
      displayMessage(
        setError,
        err.message || "Erreur chargement itérations/sprints",
      );
      setIterations([]);
    }
  }, [projectId, apiService, displayMessage]);

  const fetchProjectRoleFocus = useCallback(
    async (circleId) => {
      if (!circleId) {
        setProjectRoleFocus([]);
        return;
      }
      try {
        const data = await apiService.get(
          `circle-focus?parent_id=${circleId}`,
        );
        setProjectRoleFocus(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erreur chargement focus roles:", err);
        setProjectRoleFocus([]);
      }
    },
    [apiService],
  );

  useEffect(() => {
    fetchProjectDetails();
    fetchIterationsAndSprints();
    fetchAffaires();
    fetchClients();
    fetchEmployees();
    fetchCircles();
    fetchSlots();
    fetchObjectives();
    fetchExpenses();
    fetchJournalEntries();
    fetchProjectStock();
    fetchAttributes();
    fetchAttributeHistory();
    fetchAttributeTargets();
  }, [
    projectId,
    fetchProjectDetails,
    fetchIterationsAndSprints,
    fetchAffaires,
    fetchClients,
    fetchEmployees,
    fetchCircles,
    fetchSlots,
    fetchObjectives,
    fetchExpenses,
    fetchJournalEntries,
    fetchProjectStock,
    fetchAttributes,
    fetchAttributeHistory,
    fetchAttributeTargets,
  ]);

  useEffect(() => {
    if (!projectId) return;
    const userKey = user?.id || user?.email || "current";
    const storageKey = `knowledge_drive_sync:${userKey}:${projectId}`;
    const now = Date.now();
    try {
      const lastSync = Number(localStorage.getItem(storageKey) || 0);
      if (lastSync && now - lastSync < DRIVE_KNOWLEDGE_SYNC_TTL_MS) {
        return;
      }
      localStorage.setItem(storageKey, String(now));
    } catch {
      // Storage can be unavailable in private contexts; Drive sync remains best effort.
    }
    apiService.syncKnowledgeProjectDrive(projectId).catch((err) => {
      if (process.env.REACT_APP_API_DEBUG === "1") {
        console.debug("Drive Knowledge sync skipped:", err);
      }
    });
  }, [projectId, user?.id, user?.email]);

  const focusParentCircleId = useMemo(() => {
    if (project?.circle_id === null || project?.circle_id === undefined) {
      return null;
    }
    const circleId = Number(project.circle_id);
    if (Number.isNaN(circleId)) return null;
    const circle = (circles || []).find((item) => Number(item.id) === circleId);
    if (!circle) return circleId;
    const hasChildren = (circles || []).some(
      (child) => Number(child.parent_id) === circleId,
    );
    const isRoleCircle =
      circle.parent_id !== null && circle.parent_id !== undefined && !hasChildren;
    return isRoleCircle ? circle.parent_id : circleId;
  }, [project?.circle_id, circles]);

  useEffect(() => {
    fetchProjectRoleFocus(focusParentCircleId);
  }, [focusParentCircleId, fetchProjectRoleFocus]);

  const handleAddProjectRoleFocus = useCallback(
    async (circleId, employeeId) => {
      if (!project?.id) return false;
      const parsedCircleId = Number(circleId);
      const parsedEmployeeId = Number(employeeId);
      if (Number.isNaN(parsedCircleId) || Number.isNaN(parsedEmployeeId)) {
        return false;
      }

      const projectIdValue = Number(project.id);
      if (Number.isNaN(projectIdValue)) return false;
      const projectLabel = (project.name || "Projet").replace("]", ")");
      const projectTokenPrefix = `@[project:${projectIdValue}|`;
      const projectMention = `@[project:${projectIdValue}|${projectLabel}]`;

      const existingEntry = (projectRoleFocus || []).find(
        (entry) =>
          Number(entry?.circle_id) === parsedCircleId &&
          Number(entry?.employee_id) === parsedEmployeeId,
      );
      const existingText =
        typeof existingEntry?.focus_text === "string"
          ? existingEntry.focus_text.trim()
          : "";

      let nextText = existingText;
      if (!existingText) {
        nextText = projectMention;
      } else if (!existingText.includes(projectTokenPrefix)) {
        nextText = `${existingText}\n${projectMention}`;
      }

      if (nextText === existingText && existingText.includes(projectTokenPrefix)) {
        return true;
      }

      try {
        const response = await apiService.put("/circle-focus", {
          circle_id: parsedCircleId,
          employee_id: parsedEmployeeId,
          focus_text: nextText,
        });
        if (response) {
          setProjectRoleFocus((prev) => {
            const list = Array.isArray(prev) ? [...prev] : [];
            const index = list.findIndex(
              (entry) =>
                Number(entry?.circle_id) === parsedCircleId &&
                Number(entry?.employee_id) === parsedEmployeeId,
            );
            if (index >= 0) {
              list[index] = { ...list[index], ...response };
            } else {
              list.push(response);
            }
            return list;
          });
        } else {
          fetchProjectRoleFocus(focusParentCircleId);
        }
        return true;
      } catch (err) {
        displayMessage(
          setError,
          `Erreur ajout focus: ${err?.message || "inconnue"}`,
        );
        return false;
      }
    },
    [
      apiService,
      displayMessage,
      fetchProjectRoleFocus,
      focusParentCircleId,
      project,
      projectRoleFocus,
    ],
  );

  const handleRemoveProjectRoleFocus = useCallback(
    async (circleId, employeeId) => {
      if (!project?.id) return false;
      const parsedCircleId = Number(circleId);
      const parsedEmployeeId = Number(employeeId);
      if (Number.isNaN(parsedCircleId) || Number.isNaN(parsedEmployeeId)) {
        return false;
      }

      const projectIdValue = Number(project.id);
      if (Number.isNaN(projectIdValue)) return false;
      const mentionRegex = new RegExp(
        `@\\[project:${projectIdValue}\\|[^\\]]+\\]`,
        "g",
      );

      const existingEntry = (projectRoleFocus || []).find(
        (entry) =>
          Number(entry?.circle_id) === parsedCircleId &&
          Number(entry?.employee_id) === parsedEmployeeId,
      );
      const existingText =
        typeof existingEntry?.focus_text === "string"
          ? existingEntry.focus_text
          : "";
      if (!existingText) return false;

      const cleaned = existingText.replace(mentionRegex, "");
      const nextText = cleaned
        .split(/\r?\n/)
        .map((line) => line.replace(/\s{2,}/g, " ").trim())
        .filter(Boolean)
        .join("\n");
      const nextPayload = nextText || null;

      try {
        const response = await apiService.put("/circle-focus", {
          circle_id: parsedCircleId,
          employee_id: parsedEmployeeId,
          focus_text: nextPayload,
        });
        setProjectRoleFocus((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const index = list.findIndex(
            (entry) =>
              Number(entry?.circle_id) === parsedCircleId &&
              Number(entry?.employee_id) === parsedEmployeeId,
          );
          if (!nextPayload) {
            if (index >= 0) list.splice(index, 1);
            return list;
          }
          const nextEntry =
            response ||
            (index >= 0
              ? { ...list[index], focus_text: nextText }
              : {
                circle_id: parsedCircleId,
                employee_id: parsedEmployeeId,
                focus_text: nextText,
              });
          if (index >= 0) {
            list[index] = nextEntry;
          } else {
            list.push(nextEntry);
          }
          return list;
        });
        return true;
      } catch (err) {
        displayMessage(
          setError,
          `Erreur suppression focus: ${err?.message || "inconnue"}`,
        );
        return false;
      }
    },
    [apiService, displayMessage, project, projectRoleFocus],
  );

  useEffect(() => {
    if (!lastMutation || !lastMutation.metadata) {
      return;
    }
    const metadata = lastMutation.metadata;
    const rawProjectId = metadata.project_id ?? metadata.projectId;
    const parsedProjectId =
      rawProjectId !== undefined && rawProjectId !== null
        ? Number(rawProjectId)
        : null;
    if ((parsedProjectId === null || Number.isNaN(parsedProjectId)) && lastMutation.source !== 'mcp') {
      return;
    }
    if (parsedProjectId !== null && !Number.isNaN(parsedProjectId) && Number(projectId) !== parsedProjectId) {
      return;
    }
    const entity = (metadata.entity || "").toLowerCase();
    const action = (metadata.action || "").toLowerCase();
    const refreshIterations = ["iterations", "sprints", "timeline"].includes(
      entity,
    );
    const refreshObjectives =
      entity === "objectives" || entity === "tasks" || metadata.objective_id;
    if (entity === "projects") {
      fetchProjectDetails();
    }
    if (refreshIterations) {
      fetchIterationsAndSprints();
    }
    if (refreshObjectives) {
      fetchObjectives();
    }
    if (entity === "expenses") {
      fetchExpenses();
    }
    if (entity === "journal") {
      fetchJournalEntries();
    }
    if (entity === "inventory" || entity === "stock") {
      fetchProjectStock();
    }
    if (entity === "affaires" || entity === "billing") {
      fetchAffaires();
    }
    if (entity === "attributes") {
      fetchAttributes();
      fetchAttributeHistory();
    }
    if (entity === "projects" && action === "create") {
      fetchAffaires();
    }
  }, [
    lastMutation,
    projectId,
    fetchAffaires,
    fetchExpenses,
    fetchIterationsAndSprints,
    fetchJournalEntries,
    fetchObjectives,
    fetchProjectDetails,
    fetchProjectStock,
    fetchAttributes,
    fetchAttributeHistory,
  ]);

  const fetchHoursForSprint = useCallback(
    async (sprint) => {
      if (!sprint || !sprint.id || !sprint.start_date || !sprint.end_date)
        return;
      try {
        const data = await apiService.get(
          `hours/project/${projectId}?start_date=${sprint.start_date}&end_date=${sprint.end_date}`,
        );
        setSprintHours((prev) => ({
          ...prev,
          [sprint.id]: data.total_hours || 0,
        }));
      } catch (err) {
        console.warn("Erreur chargement heures sprint:", err);
      }
    },
    [projectId, apiService],
  );

  useEffect(() => {
    const allSprints = iterations.flatMap((iter) => iter.sprints || []);
    allSprints.forEach(fetchHoursForSprint);
  }, [iterations, fetchHoursForSprint]);

  const handleGenericUpdate = useCallback(
    async (url, method, body, errorMsgPrefix) => {
      try {
        const options = { method };
        const isJsonBody =
          method === "POST" || method === "PATCH" || method === "PUT";

        if (body && isJsonBody) {
          // Explicitement stringifier le corps pour les requêtes JSON.
          // apiService.fetch devrait ensuite s'assurer que le Content-Type: application/json est défini.
          options.body = JSON.stringify(body);
          // Si apiService.fetch ne gère pas le Content-Type, il faudrait l'ajouter ici ou dans apiService.
          // options.headers = { ...options.headers, 'Content-Type': 'application/json' };
        } else if (body) {
          // Pour FormData ou autres types de corps, passer directement.
          options.body = body;
        }

        // S'assurer que l'URL est relative si apiBaseUrl est utilisé par apiService.
        const relativeUrl = url.startsWith(apiBaseUrl)
          ? url.substring(apiBaseUrl.length)
          : url;
        const finalUrl = relativeUrl.startsWith("/")
          ? relativeUrl.substring(1)
          : relativeUrl;

        const data = await apiService.fetch(finalUrl, options);
        return data;
      } catch (err) {
        let errorMessage = `${errorMsgPrefix}: Erreur inconnue`;
        // Amélioration de l'affichage des messages d'erreur 422
        if (err && err.message) {
          if (typeof err.message === "string") {
            errorMessage = `${errorMsgPrefix}: ${err.message}`;
          } else if (typeof err.message === "object") {
            if (
              err.message.detail &&
              Array.isArray(err.message.detail) &&
              err.message.detail.length > 0
            ) {
              const firstError = err.message.detail[0];
              const fieldPath = firstError.loc
                ? firstError.loc.join(".")
                : "inconnu";
              errorMessage = `${errorMsgPrefix}: ${firstError.msg} (champ: ${fieldPath})`;
            } else if (
              err.message.detail &&
              typeof err.message.detail === "string"
            ) {
              errorMessage = `${errorMsgPrefix}: ${err.message.detail}`;
            } else {
              try {
                errorMessage = `${errorMsgPrefix}: ${JSON.stringify(err.message)}`;
              } catch {
                /* Ignore */
              }
            }
          }
        }
        console.error(
          `${errorMsgPrefix} Erreur (formatée):`,
          errorMessage,
          err,
        );
        displayMessage(setError, errorMessage);
        throw err;
      }
    },
    [apiService, displayMessage, apiBaseUrl],
  ); // apiBaseUrl est une dépendance

  const handleFormDataUpdate = useCallback(
    async (url, method, formData, errorMsgPrefix) => {
      try {
        const data = await apiService.uploadFile(
          url.startsWith("/") ? url.substring(1) : url,
          formData,
          null,
          method,
        );
        return data;
      } catch (err) {
        console.error(`${errorMsgPrefix} Catch Erreur:`, err);
        displayMessage(
          setError,
          err.message || `${errorMsgPrefix}: Erreur inconnue`,
        );
        throw err;
      }
    },
    [apiService, displayMessage],
  );

  const handleProjectPatch = useCallback(
    async (patchData) => {
      try {
        const updatedProject = await handleGenericUpdate(
          `/projects/${projectId}`,
          "PATCH",
          patchData,
          "Mise à jour projet",
        );
        if (updatedProject) setProject(updatedProject);
        else fetchProjectDetails();
      } catch (err) {
        /* Error already displayed */
      }
    },
    [projectId, handleGenericUpdate, fetchProjectDetails],
  );

  const handleNameUpdate = useCallback(
    (newName) => handleProjectPatch({ name: newName }),
    [handleProjectPatch],
  );
  // --- MODIFICATION : Ajout du handler pour le numéro ---
  const handleNumberUpdate = useCallback(
    (newNumber) => handleProjectPatch({ project_number: newNumber }),
    [handleProjectPatch],
  );
  const handleVisionUpdate = useCallback(
    (newVision) => handleProjectPatch({ vision: newVision }),
    [handleProjectPatch],
  );
  const handleBriefUpdate = useCallback(
    (newBrief) => handleProjectPatch({ brief: newBrief }),
    [handleProjectPatch],
  );
  const handleCircleUpdate = useCallback(
    (newCircleId) =>
      handleProjectPatch({
        circle_id: newCircleId === "" ? null : parseInt(newCircleId, 10),
      }),
    [handleProjectPatch],
  );
  const handleLinkUpdate = useCallback(
    (field, value) => handleProjectPatch({ [field]: value }),
    [handleProjectPatch],
  );
  const handleHeroImageUpload = useCallback(
    async (file) => {
      if (!file) return;
      const formData = new FormData();
      formData.append("file", file);
      try {
        const updatedProject = await apiService.uploadFile(
          `projects/${projectId}/hero-image`,
          formData,
          null,
          "POST",
        );
        if (updatedProject) {
          setProject(updatedProject);
        }
      } catch (err) {
        console.error("Erreur upload image projet:", err);
        displayMessage(
          setError,
          err.message || "Impossible de mettre à jour l'image du projet.",
        );
      }
    },
    [apiService, projectId, displayMessage],
  );

  const handleHeroImageRemove = useCallback(async () => {
    try {
      const updatedProject = await apiService.delete(
        `/projects/${projectId}/hero-image`,
      );
      if (updatedProject) {
        setProject(updatedProject);
      }
    } catch (err) {
      console.error("Erreur suppression image projet:", err);
      displayMessage(
        setError,
        err.message || "Impossible de supprimer l'image du projet.",
      );
    }
  }, [apiService, projectId, displayMessage]);
  const handleClientChange = useCallback((updatedProject) => {
    setProject(updatedProject);
  }, []);

  const updateProjectContacts = useCallback((contacts) => {
    setProject((prev) =>
      prev ? { ...prev, client_contacts: Array.isArray(contacts) ? contacts : [] } : prev,
    );
  }, []);

  const handleAddProjectContact = useCallback(
    async (contactId, roleValue) => {
      if (!projectId) return false;
      try {
        const payload = {
          contact_id: contactId,
          role_in_project: roleValue && roleValue.trim() ? roleValue.trim() : null,
        };
        const updatedContacts = await apiService.addProjectClientContact(projectId, payload);
        updateProjectContacts(updatedContacts || []);
        return true;
      } catch (err) {
        displayMessage(setError, `Erreur ajout contact: ${err.message}`);
        return false;
      }
    },
    [apiService, projectId, updateProjectContacts, displayMessage],
  );

  const handleUpdateProjectContactRole = useCallback(
    async (contactId, roleValue) => {
      if (!projectId) return false;
      try {
        const payload = {
          role_in_project: roleValue && roleValue.trim() ? roleValue.trim() : null,
        };
        const updatedContacts = await apiService.updateProjectClientContact(
          projectId,
          contactId,
          payload,
        );
        updateProjectContacts(updatedContacts || []);
        return true;
      } catch (err) {
        displayMessage(setError, `Erreur mise à jour contact: ${err.message}`);
        return false;
      }
    },
    [apiService, projectId, updateProjectContacts, displayMessage],
  );

  const handleRemoveProjectContact = useCallback(
    async (contactId) => {
      if (!projectId) return false;
      try {
        const updatedContacts = await apiService.removeProjectClientContact(projectId, contactId);
        updateProjectContacts(updatedContacts || []);
        return true;
      } catch (err) {
        displayMessage(setError, `Erreur suppression contact: ${err.message}`);
        return false;
      }
    },
    [apiService, projectId, updateProjectContacts, displayMessage],
  );

  const handleQuickIterationAdd = useCallback(
    async (iterationPayload) => {
      const payload = {
        name: iterationPayload.name || `Itération ${iterations.length + 1}`,
        start_date: iterationPayload.start_date,
        end_date: iterationPayload.end_date,
        description: iterationPayload.description || "",
      };
      try {
        await handleGenericUpdate(
          `/projects/${projectId}/iterations`,
          "POST",
          payload,
          "Ajout itération",
        );
        fetchIterationsAndSprints();
      } catch (err) {
        /* Error handling */
      }
    },
    [
      projectId,
      handleGenericUpdate,
      fetchIterationsAndSprints,
      iterations.length,
    ],
  );

  const handleDeleteIteration = useCallback(
    async (iterationId) => {
      if (!window.confirm("Supprimer cette itération et tous ses sprints ?"))
        return;
      try {
        await handleGenericUpdate(
          `/projects/${projectId}/iterations/${iterationId}`,
          "DELETE",
          null,
          "Suppression itération",
        );
        fetchIterationsAndSprints();
        setSelectedItemId((prev) =>
          (prev.type === "iteration" && prev.id === iterationId) ||
            (prev.type === "sprint" &&
              iterations
                .find((i) => i.id === iterationId)
                ?.sprints.some((s) => s.id === prev.id))
            ? { type: null, id: null }
            : prev,
        );
      } catch (err) {
        /* Error handled */
      }
    },
    [
      projectId,
      handleGenericUpdate,
      fetchIterationsAndSprints,
      iterations,
      setSelectedItemId,
    ],
  );

  const handleIterationPatch = useCallback(
    async (iterationId, patchData) => {
      try {
        const payload = { ...patchData };
        await handleGenericUpdate(
          `/projects/${projectId}/iterations/${iterationId}`,
          "PATCH",
          payload,
          "Mise à jour itération",
        );
        fetchIterationsAndSprints();
      } catch (err) {
        if (
          err.message &&
          (err.message.includes("chevauchent") ||
            err.message.includes("overlap"))
        ) {
          displayMessage(
            setError,
            "Les nouvelles dates de l'itération chevauchent une période existante.",
            5000,
          );
        }
      }
    },
    [projectId, handleGenericUpdate, fetchIterationsAndSprints, displayMessage],
  );

  const handleIterationDateUpdate = useCallback(
    (iterationId, field, value) => {
      handleIterationPatch(iterationId, { [field]: value });
    },
    [handleIterationPatch],
  );

  const handleIterationInlineUpdate = useCallback(
    (iterationId, field, value) => {
      handleIterationPatch(iterationId, { [field]: value });
    },
    [handleIterationPatch],
  );

  const handleIterationVisionUpdate = useCallback(
    (iterationId, newDescription) => {
      handleIterationPatch(iterationId, { description: newDescription });
    },
    [handleIterationPatch],
  );
  const handleIterationTimelineUpdate = useCallback(
    (iterationId, dates) => {
      handleIterationPatch(iterationId, {
        start_date: dates.start_date,
        end_date: dates.end_date,
      });
    },
    [handleIterationPatch],
  );

  const handleTimelineAddIteration = useCallback(() => {
    const parseDateInput = (value) => {
      if (!value || typeof value !== "string") return null;
      const [year, month, day] = value.split("T")[0].split("-").map(Number);
      if (!year || !month || !day) return null;
      return new Date(year, month - 1, day);
    };
    const formatDateInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    const startOfDay = (date) =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const addDays = (date, days) =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
    const getNextMonday = (date) => {
      const day = date.getDay();
      const offset = (8 - day) % 7;
      return addDays(date, offset);
    };
    const withEndDates = (iterations || [])
      .map((iter) => parseDateInput(iter?.end_date))
      .filter((date) => date && !Number.isNaN(date.getTime()));
    const lastEndDate = withEndDates.length
      ? new Date(Math.max(...withEndDates.map((date) => date.getTime())))
      : null;
    const baseDate = lastEndDate ? addDays(startOfDay(lastEndDate), 1) : startOfDay(new Date());
    const iterStartDate = getNextMonday(baseDate);
    const iterEndDate = addDays(iterStartDate, 39); // 6 weeks, ending Friday
    handleQuickIterationAdd({
      start_date: formatDateInput(iterStartDate),
      end_date: formatDateInput(iterEndDate),
    });
  }, [iterations, handleQuickIterationAdd]);

  const handleTimelineAddMilestone = useCallback(() => {
    setMilestoneEditor({ mode: "create", objective: null });
  }, []);

  const handleTimelineMilestoneUpdate = useCallback(
    async (milestoneId, patchData) => {
      if (!milestoneId || !patchData?.milestone_date) return;
      try {
        await handleGenericUpdate(
          `/projects/${projectId}/objectives/${milestoneId}`,
          "PATCH",
          {
            is_milestone: true,
            milestone_date: patchData.milestone_date,
            sprint_id: null,
          },
          "Mise a jour jalon",
        );
        fetchObjectives();
      } catch (err) {
        fetchObjectives();
      }
    },
    [fetchObjectives, handleGenericUpdate, projectId],
  );

  const handleTimelineMilestoneSelect = useCallback((milestone) => {
    if (!milestone?.id) return;
    setMilestoneEditor({ mode: "edit", objective: milestone });
  }, []);

  const handleMilestoneSave = useCallback(
    async (draft) => {
      const currentObjective = milestoneEditor?.objective;
      const isEditing = Boolean(currentObjective?.id);
      const payload = {
        name: draft.name,
        description: draft.description,
        owner_id: draft.owner_id,
        status: draft.status,
        sprint_id: null,
        is_milestone: true,
        milestone_date: draft.milestone_date,
      };

      if (!isEditing) {
        Object.assign(payload, {
          project_id: Number(projectId),
          effort: 0,
          blocked_by: [],
          comment: null,
        });
      }

      setMilestoneSaving(true);
      try {
        const savedObjective = await handleGenericUpdate(
          isEditing
            ? `/projects/${projectId}/objectives/${currentObjective.id}`
            : `/projects/${projectId}/objectives`,
          isEditing ? "PATCH" : "POST",
          payload,
          isEditing ? "Mise a jour jalon" : "Ajout jalon",
        );
        setObjectives((current) =>
          isEditing
            ? current.map((objective) =>
                objective.id === savedObjective.id ? savedObjective : objective,
              )
            : [...current, savedObjective],
        );
        setMilestoneEditor(null);
      } catch (err) {
        /* Error already displayed */
        throw err;
      } finally {
        setMilestoneSaving(false);
      }
    },
    [handleGenericUpdate, milestoneEditor, projectId],
  );

  const handleTimelineMilestoneDelete = useCallback(
    async (milestoneId) => {
      if (!milestoneId) return;
      setMilestoneSaving(true);
      try {
        await handleGenericUpdate(
          `/projects/${projectId}/objectives/${milestoneId}`,
          "DELETE",
          null,
          "Suppression jalon",
        );
        setObjectives((current) =>
          current.filter((objective) => objective.id !== milestoneId),
        );
        setMilestoneEditor(null);
      } catch (err) {
        /* Error already displayed */
        throw err;
      } finally {
        setMilestoneSaving(false);
      }
    },
    [handleGenericUpdate, projectId],
  );

  const handleQuickSprintAdd = useCallback(
    async (iterationId, sprintPayload) => {
      const payload = {
        iteration_id: parseInt(iterationId, 10),
        start_date: sprintPayload.start_date,
        end_date: sprintPayload.end_date,
        description:
          sprintPayload.description ||
          `Sprint ${iterations.find((i) => i.id === iterationId)?.sprints?.length + 1 || 1}`,
        vision: sprintPayload.vision || "",
      };
      if (!payload.end_date && payload.start_date) {
        const startDate = new Date(payload.start_date);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 20); // Default duration 3 weeks (20 working days approx)
        payload.end_date = endDate.toISOString().split("T")[0];
      }
      try {
        await handleGenericUpdate(
          `/projects/${projectId}/sprints`,
          "POST",
          payload,
          "Ajout sprint",
        );
        fetchIterationsAndSprints();
      } catch (err) {
        /* Error handled */
      }
    },
    [projectId, handleGenericUpdate, fetchIterationsAndSprints, iterations],
  );

  const handleDeleteSprint = useCallback(
    async (sprintId, iterationId) => {
      if (!window.confirm("Supprimer ce sprint ?")) return;
      try {
        await handleGenericUpdate(
          `/projects/${projectId}/sprints/${sprintId}`,
          "DELETE",
          null,
          "Suppression sprint",
        );
        fetchIterationsAndSprints();
        setSelectedItemId((prev) =>
          prev.type === "sprint" && prev.id === sprintId
            ? { type: null, id: null }
            : prev,
        );
      } catch (err) {
        /* Error handled */
      }
    },
    [
      projectId,
      handleGenericUpdate,
      fetchIterationsAndSprints,
      setSelectedItemId,
    ],
  );

  const handleSprintPatch = useCallback(
    async (sprintId, patchData) => {
      try {
        const payload = { ...patchData };
        if (payload.start_date)
          payload.start_date = new Date(payload.start_date)
            .toISOString()
            .split("T")[0];
        if (payload.end_date)
          payload.end_date = new Date(payload.end_date)
            .toISOString()
            .split("T")[0];
        if (Object.keys(payload).length === 0) {
          console.warn(
            "handleSprintPatch called with no updatable data for sprint",
            sprintId,
          );
          return;
        }
        await handleGenericUpdate(
          `/projects/${projectId}/sprints/${sprintId}`,
          "PATCH",
          payload,
          "Mise à jour sprint",
        );
        fetchIterationsAndSprints();
      } catch (err) {
        console.error(
          `Error in handleSprintPatch in ProjectDetail for sprint ${sprintId}:`,
          err,
        );
      }
    },
    [projectId, handleGenericUpdate, fetchIterationsAndSprints],
  );

  const handleSprintUpdate = useCallback(
    (sprintOrEvent) => {
      let sprintId;
      let updateData;
      if (sprintOrEvent && sprintOrEvent.id && sprintOrEvent.changes) {
        sprintId = sprintOrEvent.id;
        updateData = {
          start_date: sprintOrEvent.changes.start_date,
          end_date: sprintOrEvent.changes.end_date,
        };
      } else if (sprintOrEvent && sprintOrEvent.id) {
        sprintId = sprintOrEvent.id;
        updateData = {
          start_date: sprintOrEvent.start_date,
          end_date: sprintOrEvent.end_date,
          description: sprintOrEvent.description || "",
          vision: sprintOrEvent.vision || "",
        };
      } else {
        console.error(
          "Invalid data passed to handleSprintUpdate:",
          sprintOrEvent,
        );
        displayMessage(
          setError,
          "Données invalides pour la mise à jour du sprint.",
        );
        return;
      }
      handleSprintPatch(sprintId, updateData);
    },
    [handleSprintPatch, displayMessage],
  );

  const handleTokensUpdate = useCallback((sprintId, iterationId, tokenData) => {
    console.warn(
      "handleTokensUpdate is deprecated. Tokens are calculated from objectives.",
    );
  }, []);

  const handleAddAffaire = useCallback(
    async (affaireData, offerFile, purchaseFile) => {
      const formData = new FormData();
      formData.append("project_id", projectId);
      Object.keys(affaireData).forEach((key) => {
        if (affaireData[key] !== null && affaireData[key] !== undefined) {
          if (key === "iteration_allocations") {
            formData.append(`${key}_json`, JSON.stringify(affaireData[key]));
          } else if (!["file_path", "purchase_file_path"].includes(key)) {
            if (
              key === "status" &&
              typeof affaireData[key] === "object" &&
              affaireData[key] !== null
            ) {
              formData.append(
                key,
                affaireData[key].value || String(affaireData[key]),
              );
            } else {
              formData.append(key, String(affaireData[key]));
            }
          }
        }
      });
      if (offerFile) {
        formData.append("offer_file", offerFile);
      }
      if (purchaseFile) {
        formData.append("purchase_file", purchaseFile);
      }
      try {
        const createdAffaire = await handleFormDataUpdate(
          `/affaires`,
          "POST",
          formData,
          "Ajout offre",
        );
        fetchAffaires();
        fetchProjectDetails();
        return createdAffaire;
      } catch (err) {
        throw err;
      }
    },
    [projectId, handleFormDataUpdate, fetchAffaires, fetchProjectDetails],
  );

  const handleDeleteAffaire = useCallback(
    async (affaireId) => {
      if (!window.confirm("Supprimer cette offre ?")) return;
      try {
        await handleGenericUpdate(
          `/affaires/${affaireId}`,
          "DELETE",
          null,
          "Suppression offre",
        );
        fetchAffaires();
        fetchProjectDetails();
      } catch (err) {
        /* Error handled */
      }
    },
    [handleGenericUpdate, fetchAffaires, fetchProjectDetails],
  );

  const handleUpdateAffaire = useCallback(
    async (affaireId, affaireData, offerFile = null) => {
      const url = `/affaires/${affaireId}`;
      const errorPrefix = "Mise à jour offre";
      const formData = new FormData();
      Object.keys(affaireData).forEach((key) => {
        if (affaireData[key] !== null && affaireData[key] !== undefined) {
          if (key === "iteration_allocations") {
            formData.append(`${key}_json`, JSON.stringify(affaireData[key]));
          } else if (
            ![
              "id",
              "project_id",
              "project_name",
              "created_at",
              "updated_at",
            ].includes(key) &&
            !(key === "file_path" && offerFile) &&
            key !== "purchase_file_path"
          ) {
            if (typeof affaireData[key] === "boolean") {
              formData.append(key, affaireData[key] ? "true" : "false");
            } else if (
              key === "status" &&
              typeof affaireData[key] === "object" &&
              affaireData[key] !== null
            ) {
              formData.append(
                key,
                affaireData[key].value || String(affaireData[key]),
              );
            } else {
              formData.append(key, String(affaireData[key]));
            }
          }
        }
      });
      if (offerFile) {
        formData.append("offer_file", offerFile);
      }
      try {
        const updatedAffaire = await handleFormDataUpdate(
          url,
          "PATCH",
          formData,
          errorPrefix,
        );
        fetchAffaires();
        fetchProjectDetails();
        return updatedAffaire;
      } catch (err) {
        throw err;
      }
    },
    [handleFormDataUpdate, fetchAffaires, fetchProjectDetails],
  );

  const handleAffaireFileExtract = useCallback(
    async (file) => {
      if (!file) {
        throw new Error("Aucun fichier fourni pour l'extraction.");
      }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const extractedData = await apiService.uploadFile(
          `affaires/ai-extract`,
          formData,
        );
        if (extractedData.error) {
          console.error("Erreur retournée par l'API IA:", extractedData.error);
          throw new Error(`Erreur IA: ${extractedData.error}`);
        }
        const normalized = {
          number: extractedData.offer_number || null,
          total:
            extractedData.total_ttc != null
              ? String(extractedData.total_ttc)
              : extractedData.total_ht != null
                ? String(extractedData.total_ht)
                : null,
          purchase_total: null,
          hours_planned: null,
        };
        return normalized;
      } catch (err) {
        console.error("Échec extraction IA Affaire:", err);
        displayMessage(
          setError,
          err.message || "Erreur lors de l'extraction IA",
        );
        throw err;
      }
    },
    [apiService, displayMessage],
  );

  const handleUpdateAffairePurchaseExtract = useCallback(
    async (affaireId, purchaseFile) => {
      const formData = new FormData();
      formData.append("file", purchaseFile);
      const url = `/affaires/${affaireId}/purchase-extract`;
      try {
        const updatedAffaireDataOnly = await apiService.uploadFile(
          url.substring(1),
          formData,
          null,
          "POST",
        );
        fetchAffaires();
        fetchProjectDetails();
        return updatedAffaireDataOnly;
      } catch (err) {
        throw err;
      }
    },
    [apiService, fetchAffaires, fetchProjectDetails],
  );

  // --- MODIFICATION ICI ---
  const handleAddJournalEntry = useCallback(
    async (entryData) => {
      try {
        const newEntry = await handleGenericUpdate(
          `/projects/${projectId}/journal`,
          "POST",
          entryData,
          "Ajout entrée journal",
        );
        // Mettre à jour l'état local au lieu de re-fetch
        setJournalEntries((prev) =>
          [...prev, newEntry].sort(
            (a, b) => new Date(b.entry_date) - new Date(a.created_at),
          ),
        );
        return newEntry; // Renvoyer la nouvelle entrée
      } catch (err) {
        throw err; // Propager l'erreur pour que l'appelant puisse la gérer
      }
    },
    [projectId, handleGenericUpdate],
  );

  const handleUpdateJournalEntry = useCallback(
    async (entryId, updateData) => {
      try {
        // La fonction ne met plus à jour l'état local pour éviter les re-render.
        const updatedEntry = await handleGenericUpdate(
          `/projects/${projectId}/journal/${entryId}`,
          "PATCH",
          updateData,
          "MAJ entrée journal",
        );
        return updatedEntry;
      } catch (err) {
        throw err;
      }
    },
    [projectId, handleGenericUpdate],
  );

  const handleDeleteJournalEntry = useCallback(
    async (entryId) => {
      try {
        await handleGenericUpdate(
          `/projects/${projectId}/journal/${entryId}`,
          "DELETE",
          null,
          "Suppression entrée journal",
        );
        // Mettre à jour l'état local
        setJournalEntries((prev) => prev.filter((e) => e.id !== entryId));
      } catch (err) {
        throw err;
      }
    },
    [projectId, handleGenericUpdate],
  );
  // --- FIN MODIFICATION ---

  const handleProjectStockInlineUpdate = useCallback(
    async (stockItem, updates) => {
      if (!stockItem?.id || !updates) return;
      const previous = { ...stockItem };
      setProjectStockSavingId(stockItem.id);
      applyProjectStockUpdate(stockItem.id, updates);
      try {
        await apiService.patch(`/inventory/stock/${stockItem.id}`, updates);
        setProjectStockInlineEdits((prev) => {
          if (!prev[stockItem.id]) return prev;
          const next = { ...prev };
          delete next[stockItem.id];
          return next;
        });
      } catch (err) {
        applyProjectStockUpdate(stockItem.id, previous);
        displayMessage(
          setError,
          `Erreur mise a jour: ${err.message}`,
        );
      } finally {
        setProjectStockSavingId((prev) =>
          prev === stockItem.id ? null : prev,
        );
      }
    },
    [apiService, displayMessage, applyProjectStockUpdate],
  );

  const fetchProjectStockChildren = useCallback(
    async (stockId) => {
      if (!stockId) return;
      setProjectStockLoadingChildren((prev) => ({ ...prev, [stockId]: true }));
      try {
        const data = await apiService.get(
          `/inventory/stock/${stockId}/components`,
        );
        setProjectStockChildren((prev) => ({
          ...prev,
          [stockId]: Array.isArray(data) ? data : [],
        }));
      } catch (err) {
        setProjectStockChildren((prev) => ({ ...prev, [stockId]: [] }));
      } finally {
        setProjectStockLoadingChildren((prev) => ({
          ...prev,
          [stockId]: false,
        }));
      }
    },
    [apiService],
  );

  const toggleProjectStockNode = useCallback(
    (stockId) => {
      const isExpanded = Boolean(projectStockExpandedNodes[stockId]);
      if (!isExpanded) {
        fetchProjectStockChildren(stockId);
      }
      setProjectStockExpandedNodes((prev) => {
        const next = { ...prev };
        if (isExpanded) {
          delete next[stockId];
        } else {
          next[stockId] = true;
        }
        return next;
      });
    },
    [projectStockExpandedNodes, fetchProjectStockChildren],
  );

  const fetchProjectStockLogs = useCallback(
    async (stockId) => {
      if (!stockId) return;
      try {
        const data = await apiService.get(`/inventory/stock/${stockId}/logs`);
        setProjectStockLogs((prev) => ({
          ...prev,
          [stockId]: Array.isArray(data) ? data : [],
        }));
      } catch (err) {
        setProjectStockLogs((prev) => ({ ...prev, [stockId]: [] }));
      }
    },
    [apiService],
  );

  useEffect(() => {
    if (!projectStockSelectedId) return;
    const stillVisible = filteredProjectStockItems.some(
      (item) => item.id === projectStockSelectedId,
    );
    if (!stillVisible) {
      setProjectStockSelectedId(null);
    }
  }, [filteredProjectStockItems, projectStockSelectedId]);

  useEffect(() => {
    if (!selectedProjectStockItem) return;
    fetchProjectStockChildren(selectedProjectStockItem.id);
    fetchProjectStockLogs(selectedProjectStockItem.id);
  }, [
    selectedProjectStockItem,
    fetchProjectStockChildren,
    fetchProjectStockLogs,
  ]);

  const collectProjectStockVisibleIds = (rootId) => {
    if (!rootId) return [];
    const ids = new Set();
    const stack = [rootId];
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current || ids.has(current)) continue;
      ids.add(current);
      const children = projectStockChildren[current] || [];
      const isExpanded = projectStockExpandedNodes[current] || current === rootId;
      if (isExpanded) {
        children.forEach((child) => {
          if (child?.id) stack.push(child.id);
        });
      }
    }
    return Array.from(ids);
  };

  const projectStockVisibleIds = useMemo(
    () =>
      selectedProjectStockItem
        ? collectProjectStockVisibleIds(selectedProjectStockItem.id)
        : [],
    [selectedProjectStockItem, projectStockChildren, projectStockExpandedNodes],
  );

  useEffect(() => {
    projectStockVisibleIds.forEach((id) => {
      if (!Object.prototype.hasOwnProperty.call(projectStockLogs, id)) {
        fetchProjectStockLogs(id);
      }
    });
  }, [projectStockVisibleIds, projectStockLogs, fetchProjectStockLogs]);

  const handleProjectStockAddLog = useCallback(
    async (stockId) => {
      const message = projectStockLogMessage[stockId];
      if (!message || !message.trim()) return;
      try {
        await apiService.post(`/inventory/stock/${stockId}/logs`, { message });
        setProjectStockLogMessage((prev) => ({ ...prev, [stockId]: "" }));
        fetchProjectStockLogs(stockId);
      } catch (err) {
        displayMessage(
          setError,
          `Erreur ajout note: ${err.message}`,
        );
      }
    },
    [apiService, projectStockLogMessage, fetchProjectStockLogs, displayMessage],
  );

  // Gestion du tableau des attributs
  const handleAttributeCreate = useCallback(
    async (payload) => {
      try {
        const created = await apiService.createProjectAttribute(
          projectId,
          payload,
        );
        setAttributes((prev) => sortAttributes([...(prev || []), created]));
        await fetchAttributeHistory();
        return created;
      } catch (err) {
        console.error("Erreur création attribut:", err);
        displayMessage(
          setError,
          err.message || "Erreur lors de la création de l'attribut.",
        );
        throw err;
      }
    },
    [
      projectId,
      apiService,
      sortAttributes,
      fetchAttributeHistory,
      displayMessage,
    ],
  );

  const handleAttributeUpdate = useCallback(
    async (attributeId, payload) => {
      try {
        const updated = await apiService.updateProjectAttribute(
          projectId,
          attributeId,
          payload,
        );
        setAttributes((prev) =>
          sortAttributes(
            prev.map((attr) => (attr.id === updated.id ? updated : attr)),
          ),
        );
        await fetchAttributeHistory();
        return updated;
      } catch (err) {
        console.error("Erreur mise à jour attribut:", err);
        displayMessage(
          setError,
          err.message || "Erreur lors de la mise à jour de l'attribut.",
        );
        throw err;
      }
    },
    [
      projectId,
      apiService,
      sortAttributes,
      fetchAttributeHistory,
      displayMessage,
    ],
  );

  const handleAttributeStatusUpdate = useCallback(
    async (attributeId, status, comment, changedAt) => {
      try {
        const isoTimestamp = changedAt
          ? new Date(changedAt).toISOString()
          : null;
        const payload = { status, comment };
        if (isoTimestamp) {
          payload.history_timestamp = isoTimestamp;
        }
        const updated = await apiService.updateProjectAttributeStatus(
          projectId,
          attributeId,
          payload,
        );
        setAttributes((prev) =>
          sortAttributes(
            prev.map((attr) => (attr.id === updated.id ? updated : attr)),
          ),
        );
        await fetchAttributeHistory();
        return updated;
      } catch (err) {
        console.error("Erreur changement statut attribut:", err);
        displayMessage(
          setError,
          err.message || "Erreur lors du changement de statut.",
        );
        throw err;
      }
    },
    [
      projectId,
      apiService,
      sortAttributes,
      fetchAttributeHistory,
      displayMessage,
    ],
  );

  const handleAttributeDelete = useCallback(
    async (attributeId) => {
      try {
        await apiService.deleteProjectAttribute(projectId, attributeId);
        setAttributes((prev) => prev.filter((attr) => attr.id !== attributeId));
        await fetchAttributeHistory();
        await fetchAttributeTargets();
      } catch (err) {
        console.error("Erreur suppression attribut:", err);
        displayMessage(
          setError,
          err.message || "Erreur lors de la suppression de l'attribut.",
        );
        throw err;
      }
    },
    [
      projectId,
      apiService,
      fetchAttributeHistory,
      fetchAttributeTargets,
      displayMessage,
    ],
  );

  const handleAttributeHistoryUpdate = useCallback(
    async (attributeId, historyId, payload) => {
      try {
        await apiService.updateProjectAttributeHistoryEntry(
          projectId,
          attributeId,
          historyId,
          payload,
        );
        await fetchAttributeHistory();
      } catch (err) {
        console.error("Erreur mise à jour historique attribut:", err);
        displayMessage(
          setError,
          err.message || "Impossible de mettre à jour l'historique.",
        );
        throw err;
      }
    },
    [projectId, apiService, fetchAttributeHistory, displayMessage],
  );

  const handleAttributeHistoryDelete = useCallback(
    async (attributeId, historyId) => {
      try {
        await apiService.deleteProjectAttributeHistoryEntry(
          projectId,
          attributeId,
          historyId,
        );
        await fetchAttributeHistory();
      } catch (err) {
        console.error("Erreur suppression historique attribut:", err);
        displayMessage(
          setError,
          err.message || "Impossible de supprimer l'historique.",
        );
        throw err;
      }
    },
    [projectId, apiService, fetchAttributeHistory, displayMessage],
  );

  const handleAddIterationAttributeTarget = useCallback(
    async (iterationId, payload) => {
      try {
        await apiService.createIterationAttributeTarget(iterationId, payload);
        await fetchAttributeTargets();
      } catch (err) {
        console.error("Erreur création cible attribut:", err);
        displayMessage(
          setError,
          err.message || "Impossible d'ajouter la cible.",
        );
        throw err;
      }
    },
    [apiService, fetchAttributeTargets, displayMessage],
  );

  const handleUpdateIterationAttributeTarget = useCallback(
    async (iterationId, targetId, payload) => {
      try {
        await apiService.updateIterationAttributeTarget(
          iterationId,
          targetId,
          payload,
        );
        await fetchAttributeTargets();
      } catch (err) {
        console.error("Erreur mise à jour cible attribut:", err);
        displayMessage(
          setError,
          err.message || "Impossible de mettre à jour la cible.",
        );
        throw err;
      }
    },
    [apiService, fetchAttributeTargets, displayMessage],
  );

  const handleDeleteIterationAttributeTarget = useCallback(
    async (iterationId, targetId) => {
      try {
        await apiService.deleteIterationAttributeTarget(iterationId, targetId);
        await fetchAttributeTargets();
      } catch (err) {
        console.error("Erreur suppression cible attribut:", err);
        displayMessage(
          setError,
          err.message || "Impossible de supprimer la cible.",
        );
        throw err;
      }
    },
    [apiService, fetchAttributeTargets, displayMessage],
  );

  // Determine which iteration to highlight in the summary
  const selectedIterationId = useMemo(() => {
    if (selectedItemId?.type === "iteration" && selectedItemId.id)
      return selectedItemId.id;
    if (selectedItemId?.type === "sprint" && selectedItemId.id) {
      const found = iterations.find((it) =>
        (it.sprints || []).some((s) => s.id === selectedItemId.id),
      );
      return found ? found.id : null;
    }
    return null;
  }, [selectedItemId, iterations]);

  const handleTimelineIterationSelect = useCallback(
    (iteration, options = {}) => {
      if (iteration && iteration.id) {
        setSelectedItemId({ type: "iteration", id: iteration.id });
        if (!options?.skipFocus) {
          focusPanelSection("iterations");
        }
      }
    },
    [focusPanelSection],
  );

  const handleTimelineSprintSelect = useCallback(
    (sprint, options = {}) => {
      if (sprint && sprint.id) {
        setSelectedItemId({ type: "sprint", id: sprint.id });
        if (!options?.skipFocus) {
          focusPanelSection("iterations");
        }
      }
    },
    [focusPanelSection],
  );

  const attributeTargetsByIteration = useMemo(() => {
    return attributeTargets.reduce((acc, target) => {
      if (!target || typeof target.iteration_id === "undefined") return acc;
      const key = Number(target.iteration_id);
      if (Number.isNaN(key)) return acc;
      if (!acc[key]) acc[key] = [];
      acc[key].push(target);
      return acc;
    }, {});
  }, [attributeTargets]);

  const objectiveProgress = useMemo(() => {
    if (!Array.isArray(objectives) || objectives.length === 0) {
      return { done: 0, total: objectives.length || 0, percent: 0 };
    }
    const done = objectives.filter(
      (objective) => (objective.status || "").toLowerCase() === "done",
    ).length;
    const percent = Math.round((done / objectives.length) * 100);
    return {
      done,
      total: objectives.length,
      percent: Number.isFinite(percent) ? percent : 0,
    };
  }, [objectives]);

  const activeIteration = useMemo(() => {
    if (!Array.isArray(iterations) || iterations.length === 0) return null;
    const sorted = [...iterations].sort((a, b) => {
      const startA = a.start_date
        ? new Date(a.start_date).getTime()
        : Number.MAX_SAFE_INTEGER;
      const startB = b.start_date
        ? new Date(b.start_date).getTime()
        : Number.MAX_SAFE_INTEGER;
      return startA - startB;
    });
    const now = Date.now();
    const current = sorted.find((iteration) => {
      if (!iteration.start_date || !iteration.end_date) return false;
      const start = new Date(iteration.start_date).getTime();
      const end = new Date(iteration.end_date).getTime();
      return start <= now && now <= end;
    });
    return current || sorted[0];
  }, [iterations]);

  const nextIterationToFinish = useMemo(() => {
    if (!Array.isArray(iterations) || iterations.length === 0) return null;
    const now = Date.now();
    const prepared = iterations
      .map((iteration) => {
        if (!iteration?.end_date) return null;
        const parsedEnd = new Date(iteration.end_date);
        if (Number.isNaN(parsedEnd.getTime())) return null;
        return { ...iteration, _endDate: parsedEnd };
      })
      .filter(Boolean)
      .sort((a, b) => a._endDate - b._endDate);
    if (prepared.length === 0) return null;
    const next = prepared.find((iteration) => iteration._endDate.getTime() >= now);
    return next || prepared[prepared.length - 1];
  }, [iterations]);

  const projectPurchaseSummary = useMemo(() => {
    const iterationTotals = (iterations || []).reduce(
      (acc, iteration) => {
        const budgetPurchase = Number(iteration?.budget_purchase_eur || 0);
        const consumedPurchase = Number(iteration?.consumed_purchase_eur || 0);
        const hasPurchaseFromLines = Boolean(iteration?.is_purchase_forecast_overridden);
        const forecastPurchase = Number(
          iteration?.forecast_purchase_eur !== undefined && iteration?.forecast_purchase_eur !== null
            ? iteration.forecast_purchase_eur
            : iteration?.dont_achat || 0
        );

        acc.budget += budgetPurchase;
        acc.consumed += consumedPurchase;
        acc.forecast += hasPurchaseFromLines ? budgetPurchase : forecastPurchase;
        return acc;
      },
      { budget: 0, consumed: 0, forecast: 0 }
    );

    const projectIdNumeric = Number(projectId);
    const expenseTotals = (expenses || []).reduce(
      (acc, expense) => {
        const amount = Number(expense?.amount || 0);
        if (!Number.isFinite(amount) || amount === 0) return acc; // un avoir (négatif) rend du budget

        const allocations = Array.isArray(expense?.project_allocations)
          ? expense.project_allocations
          : [];

        if (allocations.length) {
          const projectPercentage = allocations
            .filter((alloc) => Number(alloc?.project_id) === projectIdNumeric)
            .reduce((sum, alloc) => sum + (Number(alloc?.percentage) || 0), 0);

          if (projectPercentage > 0) {
            acc.allocated += amount * (projectPercentage / 100);
          }
          return acc;
        }

        if (Number(expense?.project_id) === projectIdNumeric) {
          acc.allocated += amount;
        }
        return acc;
      },
      { allocated: 0 }
    );

    return {
      budget: iterationTotals.budget,
      consumed: iterationTotals.consumed,
      forecast: iterationTotals.forecast,
      allocatedExpenses: expenseTotals.allocated,
    };
  }, [expenses, iterations, projectId]);

  const heroHighlights = useMemo(() => {
    if (!project) return [];
    const currencyFormatter = new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    });
    const dateFormatter = new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "short",
    });
    const iterationVision =
      nextIterationToFinish?.description?.trim() ||
      nextIterationToFinish?.vision?.trim() ||
      "Vision à définir";
    const iterationName = nextIterationToFinish?.name?.trim();
    const iterationEndDate = nextIterationToFinish?.end_date
      ? dateFormatter.format(new Date(nextIterationToFinish.end_date))
      : null;
    const helperParts = [];
    if (iterationName) helperParts.push(iterationName);
    if (iterationEndDate) helperParts.push(`Termine le ${iterationEndDate}`);
    const iterationHelper = helperParts.join(" · ") || "Ajoutez une itération";

    return [
      {
        id: "turnover",
        label: "CA signé",
        value: currencyFormatter.format(project.calculated_turnover || 0),
        helper: project.status_display || "",
      },
      {
        id: "invoiced",
        label: "CA facturé",
        value: currencyFormatter.format(project.invoiced_turnover || 0),
        helper: 'Lignes marquées "Facturé"',
      },
      {
        id: "purchases",
        label: "Achats projet",
        value: currencyFormatter.format(projectPurchaseSummary.forecast || 0),
        helper:
          projectPurchaseSummary.allocatedExpenses > 0
            ? `Depenses saisies ${currencyFormatter.format(projectPurchaseSummary.allocatedExpenses)}`
            : `Consomme ${currencyFormatter.format(projectPurchaseSummary.consumed || 0)}`,
      },
      {
        id: "progress",
        label: "Avancement objectifs",
        value: `${objectiveProgress.percent}%`,
        helper: `${objectiveProgress.done}/${objectiveProgress.total || 0} objectifs finalisés`,
      },
        {
          id: "milestone",
          label: "Prochaine itération",
          value: iterationVision,
          helper: iterationHelper,
        },
    ];
  }, [project, objectiveProgress, nextIterationToFinish, projectPurchaseSummary]);

  const scrollToAnchor = useCallback((anchor) => {
    if (typeof document === "undefined") return;
    const element = document.getElementById(anchor);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const iterationRangeLabel = useMemo(() => {
    if (!activeIteration?.start_date || !activeIteration?.end_date)
      return "Dates à préciser";
    const formatter = new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "short",
    });
    return `${formatter.format(new Date(activeIteration.start_date))} → ${formatter.format(new Date(activeIteration.end_date))}`;
  }, [activeIteration]);

  if (!project) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-600">
        Chargement du projet...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full px-1 py-2 sm:px-4 sm:py-4 lg:px-6">
        <div className="lg:flex lg:items-start lg:gap-5">
          <aside className="hidden lg:block lg:w-14 lg:-ml-5 lg:self-start lg:sticky lg:top-24">
            <nav className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm">
              {sideNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => scrollToAnchor(item.key)}
                    className={`relative flex h-10 w-10 items-center justify-center rounded-full border text-slate-500 transition hover:text-blue-600 ${isActive
                      ? "border-blue-200 bg-blue-50 text-blue-600 shadow"
                      : "border-transparent hover:border-slate-200"
                      }`}
                    title={item.label}
                    aria-label={item.label}
                  >
                    <Icon className="h-4 w-4" />
                    {isActive && (
                      <span className="absolute -right-1.5 h-2 w-2 rounded-full bg-blue-500" />
                    )}
                  </button>
                );
              })}
            </nav>
          </aside>
          <div className="min-w-0 flex-1 space-y-4 sm:space-y-5">
            {/* Téléphone : les sections du projet en bandeau défilant, collé en haut de l'écran. */}
            <nav className="sticky top-0 z-30 -mx-1 flex gap-1.5 overflow-x-auto border-b border-slate-200 bg-slate-50/95 px-1 py-2 backdrop-blur lg:hidden">
              {sideNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.key;
                return (
                  <button key={item.key} type="button" onClick={() => scrollToAnchor(item.key)}
                    className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${isActive
                      ? "border-blue-200 bg-blue-600 text-white"
                      : "border-slate-200 bg-white text-slate-600"}`}>
                    <Icon className="h-3.5 w-3.5" />
                    {item.label}
                  </button>
                );
              })}
            </nav>
            <section id="overview" className="scroll-mt-14 lg:scroll-mt-0">
              <ProjectHeader
                project={project}
                clients={clients}
                employees={employees}
                circles={circles}
                roleFocusEntries={projectRoleFocus}
                apiBaseUrl={apiBaseUrl}
                getAuthHeaders={getAuthHeaders}
                user={user}
                onNameUpdate={handleNameUpdate}
                onNumberUpdate={handleNumberUpdate}
                onVisionUpdate={handleVisionUpdate}
                onBriefUpdate={handleBriefUpdate}
                onClientChange={handleClientChange}
                onCircleChange={handleCircleUpdate}
                onAddClientContact={handleAddProjectContact}
                onUpdateClientContactRole={handleUpdateProjectContactRole}
                onRemoveClientContact={handleRemoveProjectContact}
                onLinkUpdate={handleLinkUpdate}
                onHeroImageUpload={handleHeroImageUpload}
                onHeroImageRemove={handleHeroImageRemove}
                onAddProjectRoleFocus={handleAddProjectRoleFocus}
                onRemoveProjectRoleFocus={handleRemoveProjectRoleFocus}
                quickNavItems={quickNavItems}
                onQuickNavClick={scrollToAnchor}
                highlights={heroHighlights}
                activeIteration={activeIteration}
                objectiveProgress={objectiveProgress}
                navigate={navigate}
              />
            </section>
            <section id="roadmap" className="space-y-3 observe-section scroll-mt-14 lg:scroll-mt-0">
              <div className="rounded-2xl border border-slate-100 bg-white p-3 shadow-sm sm:p-4">
                <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      Roadmap & capacités
                    </h2>
                    <p className="hidden text-sm text-slate-500 sm:block">
                      Itérations, sprints et slots sont alignés visuellement
                      pour planifier les cadences.
                    </p>
                  </div>
                  {timelineViewLevel === "project" && (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={handleTimelineAddMilestone}
                        className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700 hover:border-rose-300 hover:bg-white"
                      >
                        <Flag className="h-4 w-4" />
                        Nouveau jalon
                      </button>
                      <button
                        onClick={handleTimelineAddIteration}
                        className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:border-blue-300 hover:bg-white"
                      >
                        <Plus className="h-4 w-4" />
                        Nouvelle itération
                      </button>
                    </div>
                  )}
                </div>
                <div className="mt-1 sm:hidden">
                  <MobileRoadmap iterations={iterations} onIterationSelect={handleTimelineIterationSelect} />
                </div>
                <div className="mt-3 hidden sm:block">
                  <ProjectTimeline
                    project={project}
                    projectId={projectId}
                    showProjectHeader={false}
                    onViewLevelChange={setTimelineViewLevel}
                    iterations={iterations}
                    slots={slots}
                    objectives={objectives}
                    attributes={attributes}
                    iterationAttributeTargets={attributeTargetsByIteration}
                    onAddIterationAttributeTarget={
                      handleAddIterationAttributeTarget
                    }
                    onUpdateIterationAttributeTarget={
                      handleUpdateIterationAttributeTarget
                    }
                    onDeleteIterationAttributeTarget={
                      handleDeleteIterationAttributeTarget
                    }
                    onIterationUpdate={handleIterationTimelineUpdate}
                    onIterationInlineUpdate={handleIterationInlineUpdate}
                    onIterationVisionUpdate={handleIterationVisionUpdate}
                    onIterationAdd={handleTimelineAddIteration}
                    onIterationDelete={handleDeleteIteration}
                    onMilestoneUpdate={handleTimelineMilestoneUpdate}
                    onMilestoneSelect={handleTimelineMilestoneSelect}
                    onSprintAdd={handleQuickSprintAdd}
                    onSprintPatch={handleSprintPatch}
                    onSprintDelete={handleDeleteSprint}
                    onIterationSelect={handleTimelineIterationSelect}
                    onSprintSelect={handleTimelineSprintSelect}
                  />
                </div>
              </div>
            </section>

            <section id="attributes" className="space-y-3 observe-section">
              <AttributesPanel
                projectId={projectId}
                attributes={attributes}
                history={attributeHistory}
                roadmapTargets={attributeTargets}
                error={attributeError}
                isLoading={attributeLoading}
                historyLoading={attributeHistoryLoading}
                onAddAttribute={handleAttributeCreate}
                onUpdateAttribute={handleAttributeUpdate}
                onUpdateStatus={handleAttributeStatusUpdate}
                onDeleteAttribute={handleAttributeDelete}
                onUpdateHistoryEntry={handleAttributeHistoryUpdate}
                onDeleteHistoryEntry={handleAttributeHistoryDelete}
              />
            </section>

            <section id="journal" className="space-y-3 pb-6 observe-section">
              <div
                ref={journalPanelRef}
                className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      Journal de bord
                    </h2>
                    <p className="text-sm text-slate-500">
                      Capturez les décisions, signaux faibles et rituels clés du
                      projet.
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <JournalPanel
                    projectId={projectId}
                    project={project}
                    journalEntries={journalEntries}
                    onAddEntry={handleAddJournalEntry}
                    onUpdateEntry={handleUpdateJournalEntry}
                    onDeleteEntry={handleDeleteJournalEntry}
                    onFormClose={fetchJournalEntries}
                    employees={employees}
                circles={circles}
                  />
                </div>
              </div>
            </section>

            <section id="meetings" className="space-y-3 pb-6 observe-section">
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <ProjectMeetings projectId={projectId} />
              </div>
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <h2 className="text-base font-semibold text-slate-900">Points à aborder avec le client</h2>
                <p className="mb-3 text-sm text-slate-500">
                  Partagés avec l'équipe ; cochés quand ils sont abordés en réunion (à la main ou par Muse).
                </p>
                <TalkingPointsPanel projectId={Number(projectId)} meetingTitle={project?.name} />
              </div>
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <ProjectLexicon projectId={Number(projectId)} />
              </div>
            </section>

            <section id="carnet" className="space-y-3 pb-6 observe-section">
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <MuseCarnet projectId={Number(projectId)} />
              </div>
            </section>

            <section id="drive" className="space-y-3 pb-6 observe-section">
              <div
                ref={drivePanelRef}
                className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(340px,0.65fr)]"
              >
                <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">
                        Google Drive
                      </h2>
                      <p className="text-sm text-slate-500">
                        Parcourez le dossier lie au projet.
                      </p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <DriveBrowserPanel
                      projectId={projectId}
                      project={project}
                      maxHeight="560px"
                    />
                  </div>
                </div>
                <ProjectInvoicesPanel
                  projectId={projectId}
                  iterations={iterations}
                  maxHeight="560px"
                />
              </div>
            </section>

            <section id="tools" className="space-y-3 pb-6 observe-section">
              <div ref={toolsPanelRef}>
                <GuttenbergProjectPanel projectId={Number(projectId)} project={project} />
              </div>
            </section>

            <section id="expenses" className="space-y-3 pb-6 observe-section">
              <div
                ref={expensesPanelRef}
                className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      Dépenses & achats
                    </h2>
                    <p className="text-sm text-slate-500">
                      Gardez un œil sur les factures, engagements et coûts
                      réels.
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <ExpensesPanel
                    projectId={projectId}
                    expenses={expenses}
                    iterations={iterations}
                    apiBaseUrl={apiBaseUrl}
                    getAuthHeaders={getAuthHeaders}
                    selectedIterationId={selectedIterationId}
                  />
                </div>
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <ProjectPurchasesPanel
                    projectId={Number(projectId)}
                    title="Articles achetés"
                    onOpenInvoice={(expenseId) => navigate(`/expenses?facture=${expenseId}`)}
                  />
                </div>
              </div>
            </section>

            <section
              id="project-items"
              className="space-y-3 pb-6 observe-section"
            >
              <div
                ref={projectItemsPanelRef}
                className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      Items du projet
                    </h2>
                    <p className="text-sm text-slate-500">
                      Visualisez et mettez a jour les occurrences associees.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={fetchProjectStock}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
                  >
                    Actualiser
                  </button>
                </div>
                <div className="mt-3 rounded-xl border border-slate-200/70 bg-slate-50/40 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <input
                      type="text"
                      placeholder="Rechercher un item (numero, nom, notes...)"
                      value={projectStockSearch}
                      onChange={(event) =>
                        setProjectStockSearch(event.target.value)
                      }
                      className="w-full max-w-md rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    />
                    <div className="text-xs text-slate-400">
                      {filteredProjectStockItems.length} occurrences
                    </div>
                  </div>
                  {projectStockLoading ? (
                    <div className="mt-3 text-sm text-slate-500">
                      Chargement...
                    </div>
                  ) : (
                    <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[0.7fr_1.3fr]">
                      <div className="space-y-2">
                        {filteredProjectStockItems.length === 0 && (
                          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-3 text-sm text-slate-500">
                            Aucun item associe au projet.
                          </div>
                        )}
                        {filteredProjectStockItems.map((stockItem) => {
                          const isSelected =
                            stockItem.id === projectStockSelectedId;
                          return (
                            <div
                              key={stockItem.id}
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                setProjectStockSelectedId(stockItem.id)
                              }
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  setProjectStockSelectedId(stockItem.id);
                                }
                              }}
                              className={`rounded-xl border bg-white px-3 py-2.5 text-left text-sm shadow-sm transition ${
                                isSelected
                                  ? "border-slate-900 ring-1 ring-slate-200"
                                  : "border-slate-200 hover:border-slate-300"
                              }`}
                            >
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                  <div className="text-sm font-semibold text-slate-900">
                                    {stockItem.serial_number ||
                                      `#${stockItem.id}`} - {getProjectStockLabel(stockItem)}
                                  </div>
                                  <div className="mt-1 text-xs text-slate-500">
                                    {stockItem.location
                                      ? `Emplacement: ${stockItem.location}`
                                      : "Emplacement non defini"}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <span
                                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                      projectStockStatusClassMap[
                                        stockItem.status
                                      ] || "bg-gray-100 text-gray-600"
                                    }`}
                                  >
                                    {getProjectStockStatusLabel(
                                      stockItem.status,
                                    )}
                                  </span>
                                  <select
                                    value={stockItem.status}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event) =>
                                      handleProjectStockInlineUpdate(stockItem, {
                                        status: event.target.value,
                                      })
                                    }
                                    className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px]"
                                    disabled={
                                      projectStockSavingId === stockItem.id
                                    }
                                  >
                                    {projectStockStatusOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              {projectStockSavingId === stockItem.id && (
                                <div className="mt-2 text-[11px] text-slate-400">
                                  Sauvegarde en cours...
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="rounded-xl border border-slate-200/70 bg-white p-3 shadow-sm">
                        {selectedProjectStockItem ? (
                          renderProjectStockNode(selectedProjectStockItem)
                        ) : (
                          <div className="text-sm text-slate-500">
                            Selectionnez une occurrence pour voir ses details.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section id="affaires" className="space-y-3 pb-6 observe-section">
              <div
                ref={affairesPanelRef}
                className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      Affaires & offres
                    </h2>
                    <p className="text-sm text-slate-500">
                      Centralisez les propositions, extractions et allocations
                      budgétaires.
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <AffairesPanel
                    projectId={projectId}
                    affaires={affaires}
                    iterations={iterations}
                    apiBaseUrl={apiBaseUrl}
                    onAddAffaire={handleAddAffaire}
                    onUpdateAffaire={handleUpdateAffaire}
                    onDeleteAffaire={handleDeleteAffaire}
                    onAffaireFileExtract={handleAffaireFileExtract}
                    onUpdateAffairePurchaseExtract={
                      handleUpdateAffairePurchaseExtract
                    }
                    getAuthHeaders={getAuthHeaders}
                    sidebarWidth={sidebarWidth}
                  />
                </div>
              </div>
            </section>

            </div>
        </div>
      </div>
      <MilestoneObjectiveModal
        open={Boolean(milestoneEditor)}
        objective={milestoneEditor?.objective || null}
        projectName={project?.name}
        employees={employees}
        defaultDate={formatDateInput(new Date())}
        saving={milestoneSaving}
        onClose={() => !milestoneSaving && setMilestoneEditor(null)}
        onSave={handleMilestoneSave}
        onDelete={(objective) => handleTimelineMilestoneDelete(objective?.id)}
      />
    </div>
  );
};

export default ProjectDetail;
